import React, { createContext, useContext, useEffect, useState } from 'react';

import { FEATURE_FLAG } from '@qality/common-config';
import { Folder, TestCaseInFolder } from '@qality/common-model';
import { FlagService, FolderService } from '@qality/common-service';

import { useTestCaseRepositoryFiltersContext } from '../TestCaseRepositoryFilters';
import { useFoldersTree } from '../hooks/useFoldersTree';

export type TestCaseRepositoryFoldersContextValue = {
  isLoading: boolean;
  folders: Array<Folder>;
  currentFolder: Folder;
  currentView: 'all-test-cases' | 'folder';
  openFolder: (folder: Folder, shouldFetchFolder?: boolean) => Promise<void>;
  openAllTestCases: () => void;
  addToFolder: (
    folderId: Folder['id'],
    testCasesIds: Array<TestCaseInFolder>
  ) => void;
  deleteFromFolder: (
    folderId: Folder['id'],
    testCasesInFolder: Array<TestCaseInFolder>
  ) => void;
  addFolder: (newFolder: Folder) => void;
  deleteFolder: (folder: Folder) => void;
  getFolderById: (folderId: Folder['id']) => Folder | null;
  getSubfolders: (folder: Folder) => Array<Folder>;
  fetchFolderById: (folderId: Folder['id']) => Promise<Folder>;
  updateFolder: (folder: Folder) => void;
  expandFolder: (folder: Folder) => Promise<void>;
  collapseFolder: (folder: Folder) => void;
  isFolderExpanded: (folder: Folder) => boolean;
};

export const initialContextValue: TestCaseRepositoryFoldersContextValue = {
  isLoading: false,
  folders: [],
  currentFolder: null,
  openFolder: () => undefined,
  openAllTestCases: () => undefined,
  currentView: 'all-test-cases',
  addToFolder: () => undefined,
  deleteFromFolder: () => undefined,
  addFolder: () => undefined,
  deleteFolder: () => undefined,
  getFolderById: () => undefined,
  fetchFolderById: () => undefined,
  getSubfolders: () => undefined,
  updateFolder: () => undefined,
  expandFolder: () => undefined,
  collapseFolder: () => undefined,
  isFolderExpanded: () => false,
};

export const TestCaseRepositoryFoldersContext =
  createContext<TestCaseRepositoryFoldersContextValue>(initialContextValue);

export const useTestCaseRepositoryFoldersContext =
  (): TestCaseRepositoryFoldersContextValue => {
    return useContext(TestCaseRepositoryFoldersContext);
  };

type Props = { children: React.ReactNode };

export default function TestCaseRepositoryFoldersProvider({
  children,
}: Props): JSX.Element {
  const { resetFiltersToDefaults } = useTestCaseRepositoryFiltersContext();

  const [isLoading, setIsLoading] = useState(true);
  const [folders, setFolders] = useState<Array<Folder>>([]);
  const [currentFolderId, setCurrentFolderId] = useState<Folder['id'] | null>(
    null
  );

  const foldersTree = useFoldersTree();

  const initPremium = async () => {
    setIsLoading(true);

    try {
      const newFolders = await FolderService.getRootFolders();

      setFolders(newFolders);
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        "We couldn't fetch folders. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const initFree = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    FEATURE_FLAG.isPremium ? initPremium() : initFree();
  }, []);

  const openFolder = async (folder: Folder) => {
    const hasUnloadedSubfolders =
      folder.subfoldersIds.length !== folder.totalSubfolders;
    if (hasUnloadedSubfolders) {
      await fetchFolderById(folder.id);
    }

    setCurrentFolderId(folder.id);
  };

  const expandFolder = async (folder: Folder) => {
    const hasUnloadedSubfolders =
      folder.subfoldersIds.length !== folder.totalSubfolders;
    if (hasUnloadedSubfolders) {
      await fetchFolderById(folder.id);
    }

    foldersTree.expandFolder(folder.id);
  };

  const collapseFolder = (folder: Folder) => {
    foldersTree.collapseFolder(folder.id);
  };

  const isFolderExpanded = (folder: Folder) => {
    return foldersTree.isFolderExpanded(folder.id);
  };

  const fetchFolderById = async (folderId: Folder['id']): Promise<Folder> => {
    const { newFolder, newSubfolders } = await FolderService.getFolder(
      folderId
    );

    setFolders((oldFolders) => [
      ...getUpdatedFolders(newFolder, oldFolders),
      ...newSubfolders,
    ]);

    return newFolder;
  };

  const getSubfolders = (folder: Folder) => {
    return folder.subfoldersIds.map(getFolderById).filter(Boolean);
  };

  const openAllTestCases = () => {
    setCurrentFolderId(null);
    resetFiltersToDefaults();
  };

  const addToFolder = (
    folderId: Folder['id'],
    testCasesInFolder: Array<TestCaseInFolder>
  ) => {
    const updatedFolders = folders.map((folderArg) => {
      if (folderArg.id === folderId) {
        return {
          ...folderArg,
          testCases: [...folderArg.testCases, ...testCasesInFolder],
          totalTestCases: folderArg.totalTestCases + testCasesInFolder.length,
        };
      }
      return folderArg;
    });
    setFolders(updatedFolders);
  };

  const getUpdatedFolders = (
    updatedFolder: Folder,
    oldFolders?: Array<Folder>
  ) => {
    const foldersToUse = oldFolders ?? folders;
    return foldersToUse.map((folder) => {
      if (folder.id === updatedFolder.id) {
        return updatedFolder;
      }
      return folder;
    });
  };

  const updateFolder = (updatedFolder: Folder) => {
    const updatedFolders = getUpdatedFolders(updatedFolder);
    setFolders(updatedFolders);
  };

  const deleteFromFolder = (
    folderId: Folder['id'],
    testCasesInFolder: Array<TestCaseInFolder>
  ) => {
    const updatedFolders = folders.map((folderArg) => {
      if (folderArg.id === folderId) {
        return {
          ...folderArg,
          testCases: folderArg.testCases.filter(
            (testCase) =>
              !testCasesInFolder.some(
                (testCaseToDelete) => testCaseToDelete.id === testCase.id
              )
          ),
          totalTestCases: folderArg.totalTestCases - testCasesInFolder.length,
        };
      }
      return folderArg;
    });

    setFolders(updatedFolders);
  };

  const addFolder = (newFolder: Folder) => {
    if (newFolder.parentId) {
      addSubfolder(newFolder);
      return;
    }

    setFolders([newFolder, ...folders]);
  };

  const addSubfolder = (newFolder: Folder) => {
    const parentFolder = getFolderById(newFolder.parentId);
    parentFolder.subfoldersIds.unshift(newFolder.id);
    parentFolder.totalSubfolders += 1;

    const updatedFolders = getUpdatedFolders(parentFolder);

    setFolders([newFolder, ...updatedFolders]);
  };

  const deleteFolder = (deletedFolder: Folder) => {
    if (deletedFolder.parentId) {
      deleteSubfolder(deletedFolder);
      return;
    }

    setFolders((oldFolders) =>
      oldFolders.filter((folder) => folder.id !== deletedFolder.id)
    );
  };

  const deleteSubfolder = (deletedFolder: Folder) => {
    const parentFolder = getFolderById(deletedFolder.parentId);
    parentFolder.subfoldersIds = [
      ...parentFolder.subfoldersIds.filter(
        (subfolderId) => subfolderId !== deletedFolder.id
      ),
    ];
    parentFolder.totalSubfolders -= 1;

    const updatedFolders = getUpdatedFolders(parentFolder);

    setFolders(
      updatedFolders.filter((folder) => folder.id !== deletedFolder.id)
    );
  };

  const getFolderById = (folderId: Folder['id']) => {
    return folders.find((folder) => folder.id === folderId);
  };

  const currentFolder = currentFolderId ? getFolderById(currentFolderId) : null;

  const currentView = currentFolder ? 'folder' : 'all-test-cases';

  return (
    <TestCaseRepositoryFoldersContext.Provider
      value={{
        isLoading,
        folders,
        currentFolder,
        currentView,
        openFolder,
        openAllTestCases,
        addToFolder,
        deleteFromFolder,
        addFolder,
        deleteFolder,
        getFolderById,
        fetchFolderById,
        getSubfolders,
        updateFolder,
        expandFolder,
        collapseFolder,
        isFolderExpanded,
      }}
    >
      {children}
    </TestCaseRepositoryFoldersContext.Provider>
  );
}
